import React from "react";
import { Box, LinearProgress, Typography, useTheme } from "@mui/material";
import {
  useActionData,
  useLoaderData,
  useRouteLoaderData,
} from "react-router-dom";
import CouponCode from "@/components/CouponCode";
import { GetSubscriptionsResponse } from "@/types/api/subscriptions";
import { GetAccountSubscriptionResponse } from "@/types/api/accountSubscription";
import { PlanSelectionActionResponse } from "@/routes/PlansPage/types";
import { Subscription } from "@/routes/AccountSettings/Plans/types";
import { formatNumber } from "@tiny/utils/formatUtils";
import PlanTiersList from "@/components/PlanTiersList/PlanTiersList";
import PlanSelection from "../../../components/PlanSelection";
import { subscriptionMapper } from "./mapper";

function Plans() {
  const theme = useTheme();

  const dataLoaderResponse = useLoaderData() as GetSubscriptionsResponse;
  const actionData = useActionData() as PlanSelectionActionResponse;

  const accountSubscriptionData = useRouteLoaderData("main") as {
    planInfo: GetAccountSubscriptionResponse;
  };

  const { price, maxCustomerCount, currentCustomerCount } =
    accountSubscriptionData.planInfo;

  const appliedCouponPrices: { [key: string]: Subscription } =
    actionData?.subscriptions?.reduce(
      (accumulator, currentValue) => ({
        ...accumulator,
        [currentValue.id]: currentValue,
      }),
      {}
    );

  const data = dataLoaderResponse?.subscriptions?.map((s) =>
    subscriptionMapper(
      s,
      appliedCouponPrices?.[s.id],
      accountSubscriptionData.planInfo
    )
  );

  const progressPercentage =
    maxCustomerCount && currentCustomerCount
      ? (currentCustomerCount / maxCustomerCount) * 100
      : 0;

  const getProgressColor = () => {
    if (progressPercentage >= 90) return theme.palette.error.main;
    if (progressPercentage >= 70) return theme.palette.warning.main;
    return theme.palette.primary.main;
  };

  return (
    <Box mt={6}>
      <CouponCode couponFormAction="/main/account-settings/plans" />

      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
        gap={4}
        mt={4}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <PlanSelection
            plans={data}
            sx={{
              maxWidth: "100%",
              gridTemplateColumns: "1fr !important",
            }}
          />

          <Box
            py={4}
            px={4}
            bgcolor="white"
            borderRadius={2}
            sx={{ width: "100%" }}
          >
            {maxCustomerCount && currentCustomerCount !== undefined && (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="label1" color="text.secondary">
                    ${price} / month
                  </Typography>
                  <Typography variant="label1" color="text.secondary">
                    {formatNumber(currentCustomerCount)} /{" "}
                    {formatNumber(maxCustomerCount)} customers
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(progressPercentage, 100)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: theme.palette.grey[200],
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 4,
                      backgroundColor: getProgressColor(),
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Box
          py={4}
          px={4}
          bgcolor="white"
          borderRadius={2}
          sx={{ width: "100%" }}
        >
          <Typography variant="h3" fontWeight={600} sx={{ mb: 2 }}>
            Pricing Plans
          </Typography>
          <PlanTiersList
            currentPrice={price}
            sx={{ maxHeight: 380, borderRadius: 1 }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export { Plans };
